import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import TeamInvitationsCard from "../components/TeamInvitationsCard";
import { useAuth } from "../auth/AuthContext";
import { formatDate, formatDateTime } from "../lib/dateFormat";
import {
  API_BASE_URL,
  api,
  ApiError,
  type AdminDashboardPayload,
  type DashboardCommunicationBrief,
  type AdminDashboardTournamentRow,
  type DashboardMatchBrief,
} from "../lib/api";
import { tournamentTypeLabel } from "../lib/playerLabels";
import { courtNoticeName } from "../lib/courts";

function formatTypeLabel(value: string): string {
  return tournamentTypeLabel(value);
}

function formatStatusLabel(value: string): string {
  switch (value) {
    case "draft":
      return "Draft";
    case "published":
      return "Publicado";
    case "active":
      return "Activo";
    case "closed":
      return "Cerrado";
    default:
      return value;
  }
}

function resolveTournamentLogoUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  const appBase = API_BASE_URL.replace(/\/api\/v2\/?$/, "");
  return `${appBase}${logoUrl.startsWith("/") ? logoUrl : `/${logoUrl}`}`;
}

function matchPlayers(match: DashboardMatchBrief): string {
  const player1 = match.player1?.display_name ?? "Jugador 1";
  const player2 = match.player2?.display_name ?? "Jugador 2";
  return `${player1} vs ${player2}`;
}

function matchTitle(match: DashboardMatchBrief): string {
  const pieces = [match.round_display_label ?? match.round_label, match.group?.name, match.match_number ? `Partido ${match.match_number}` : null].filter(Boolean);
  return pieces.length > 0 ? pieces.join(" · ") : match.tournament_name ?? "Partido";
}

function matchMeta(match: DashboardMatchBrief): string {
  const time = match.scheduled_time ? ` · ${match.scheduled_time}` : "";
  // «Cancha 2» / «Cancha 9-A»; un nombre de texto («Central») tal cual, como en el aviso.
  const courtName = courtNoticeName(match.court);
  const court = courtName ? ` · ${courtName}` : "";
  return `${matchPlayers(match)}${time}${court}`;
}

function dateBadge(value: string | null): string {
  if (!value) return "S/F";
  const target = new Date(`${value}T00:00:00`);
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tomorrow = new Date(todayOnly);
  tomorrow.setDate(todayOnly.getDate() + 1);
  if (target.getTime() === todayOnly.getTime()) return "HOY";
  if (target.getTime() === tomorrow.getTime()) return "MAÑ";
  return formatDate(value, "Sin fecha");
}

function CommLine({ comm }: { comm: DashboardCommunicationBrief }) {
  return (
    <li>
      <strong>
        <Link to={`/admin/tournaments/${comm.tournament_id}/communications`}>
          {comm.title}
        </Link>
      </strong>
      <span className="muted">
        {comm.tournament_name ? `${comm.tournament_name} · ` : ""}
        {comm.communication_type} · {comm.audience_type}
        {comm.published_at ? ` · publicado ${formatDateTime(comm.published_at)}` : ""}
      </span>
    </li>
  );
}

function DashboardDataItem({
  badge,
  title,
  subtitle,
  tag,
  to,
}: {
  badge: string;
  title: string;
  subtitle: string;
  tag: string;
  to: string;
}) {
  return (
    <Link className="dashboard-data-item" to={to}>
      <span className="dashboard-date-pill">{badge}</span>
      <span>
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <span className="dashboard-tag">{tag}</span>
    </Link>
  );
}

/** Checklist de avance (QA Cowork #2): "4 de 6 pasos · falta: resultados
 * Grupo B". El detalle completo vive en el title (tooltip nativo). */
function todayEyebrow(): string {
  const now = new Date();
  const weekday = now.toLocaleDateString("es-MX", { weekday: "long" });
  const month = now.toLocaleDateString("es-MX", { month: "long" });
  return `Inicio · ${weekday} ${now.getDate()} de ${month}`;
}

function shortDateRange(row: AdminDashboardTournamentRow): string {
  if (!row.start_date && !row.end_date) return "Sin fechas";
  return `${formatDate(row.start_date, "Sin fecha")} – ${formatDate(row.end_date, "Sin fecha")}`;
}

function tournamentInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "T"
  );
}

/** Tarjeta de «Tus torneos en curso» (maqueta Admin-Home aprobada). */
function InProgressCard({ row }: { row: AdminDashboardTournamentRow }) {
  const logoUrl = resolveTournamentLogoUrl(row.logo_url);
  const players = row.registered ?? row.players_count;
  const pending = row.pending_results_count;
  const reports = row.pending_reports ?? 0;
  const total = row.matches_total ?? 0;
  const played = row.matches_played ?? 0;
  const pct = total > 0 ? Math.round((played / total) * 100) : 0;
  const progress = row.progress;
  const upToDate = pending === 0 && reports === 0 && (!progress || progress.missing.length === 0);
  return (
    <article className="adm-tourn__card adm-tourn__card--live">
      <div className="adm-tourn__card-top">
        <span className="adm-tourn__logo">
          {logoUrl ? <img src={logoUrl} alt={`Logo de ${row.name}`} /> : <span aria-hidden="true">{tournamentInitials(row.name)}</span>}
        </span>
        <div className="adm-tourn__card-title">
          <h3>{row.name}</h3>
          <p>
            {formatTypeLabel(row.tournament_type)} · {shortDateRange(row)} · {players} jugadores
          </p>
        </div>
      </div>
      <div className="adm-tourn__chips">
        {pending > 0 ? (
          <span className="adm-tourn__chip adm-tourn__chip--warn">
            {pending === 1 ? "1 resultado pendiente" : `${pending} resultados pendientes`}
          </span>
        ) : null}
        {reports > 0 ? (
          <Link className="adm-tourn__chip adm-tourn__chip--warn" to={`/admin/tournaments/${row.id}/result-reports`}>
            {reports === 1 ? "1 reporte por aprobar" : `${reports} reportes por aprobar`}
          </Link>
        ) : null}
        {progress && progress.missing.length > 0 ? (
          <span className="adm-tourn__chip adm-tourn__chip--upcoming" title={progress.missing.join(" · ")}>
            Paso {Math.min(progress.steps_done + 1, progress.steps_total)} de {progress.steps_total}: {progress.missing[0]}
          </span>
        ) : null}
        {upToDate ? <span className="adm-tourn__chip adm-home__chip-ok">Al día</span> : null}
      </div>
      {total > 0 ? (
        <div className="adm-tourn__progress">
          <div className="adm-tourn__progress-label">
            <span>Partidos jugados</span>
            <strong>
              {played} de {total}
            </strong>
          </div>
          <div
            className="adm-tourn__bar adm-tourn__bar--lime"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={`Partidos jugados ${played} de ${total}`}
          >
            <i style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : null}
      <div className="adm-tourn__card-actions">
        <Link className="adm-tourn__open" to={`/admin/tournaments/${row.id}`}>
          Abrir operación
        </Link>
        {pending > 0 ? (
          <Link className="adm-tourn__open adm-tourn__open--lime" to={`/admin/tournaments/${row.id}/results`}>
            Capturar resultados
          </Link>
        ) : null}
      </div>
    </article>
  );
}

export default function HomeAdminPage() {
  const { token, user } = useAuth();
  const [data, setData] = useState<AdminDashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.dashboards.admin(token);
      setData(response.data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el dashboard.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadUnread = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.notifications.listForAdmin(token, {
        limit: 1,
      });
      setUnreadCount(response.data.unread_count);
    } catch {
      setUnreadCount(null);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadUnread();
  }, [loadUnread]);

  const inProgress = useMemo(() => {
    const rows = (data?.recent_tournaments ?? []).filter(
      (t) => t.status !== "draft" && t.status !== "closed" && t.status !== "archived",
    );
    // Primero los que piden algo (resultados o reportes pendientes).
    return [...rows].sort(
      (a, b) =>
        b.pending_results_count + (b.pending_reports ?? 0) - (a.pending_results_count + (a.pending_reports ?? 0)),
    );
  }, [data]);

  const primaryTournament = inProgress[0] ?? data?.recent_tournaments[0] ?? null;
  const pendingTournament = data?.recent_tournaments.find((tournament) => tournament.pending_results_count > 0) ?? null;
  const resultsPath = pendingTournament
    ? `/admin/tournaments/${pendingTournament.id}/results`
    : primaryTournament
      ? `/admin/tournaments/${primaryTournament.id}/results`
      : "/admin/tournaments";

  const pendingResults = data?.matches.pending_results ?? 0;
  const pendingReports = data?.result_reports?.pending ?? 0;
  const reportTournaments = data?.result_reports?.by_tournament ?? [];
  const reportsPath =
    reportTournaments.length === 1
      ? `/admin/tournaments/${reportTournaments[0].tournament_id}/result-reports`
      : "/admin/tournaments";
  const receipts = data?.payments?.receipts_to_review ?? 0;
  const mpConnected = data?.payments?.mercadopago_connected ?? false;
  const firstName = (user?.full_name ?? "").trim().split(/\s+/)[0] || "admin";

  return (
    <div className="adm-players adm-home">
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}

      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">{todayEyebrow()}</span>
          <h1 className="adm-players__title">Hola, {firstName}</h1>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--primary sa-link-btn" to="/admin/tournaments/new">
            + Nuevo torneo
          </Link>
        </div>
      </header>

      <TeamInvitationsCard token={token} onAccepted={() => void load()} />

      <section className="adm-home__today" aria-labelledby="adm-home-today">
        <h2 id="adm-home-today" className="adm-home__today-title">Qué necesita atención hoy</h2>
        <div className="adm-home__tiles">
          <Link className={`adm-home__tile${pendingResults > 0 ? " adm-home__tile--lime" : ""}`} to={resultsPath}>
            <strong>{pendingResults}</strong>
            <b>Resultados sin capturar</b>
            <small>{pendingResults > 0 ? "Partidos con fecha pasada · Capturar ›" : "Todo al día"}</small>
          </Link>
          <Link className="adm-home__tile" to={reportsPath}>
            <strong>{pendingReports}</strong>
            <b>Reportes por aprobar</b>
            <small>{pendingReports > 0 ? "Resultados que mandaron los jugadores ›" : "Todo al día"}</small>
          </Link>
          <Link className="adm-home__tile" to="/admin/finance?status=pending&period=all">
            <strong>{receipts}</strong>
            <b>Comprobantes por revisar</b>
            <small>{receipts > 0 ? "Pagos por fuera ›" : "Todo al día"}</small>
          </Link>
        </div>
        <div className="adm-home__chips" aria-label="Resumen rápido">
          <Link to="/admin/tournaments">
            {data?.tournaments.active ?? 0} torneos activos · {data?.tournaments.draft ?? 0} en borrador
          </Link>
          <Link to="/admin/players">{data?.players.total_active ?? 0} jugadores</Link>
          <span>
            {data?.matches.today ?? 0} partidos hoy · {data?.matches.upcoming ?? 0} próximos
          </span>
          <Link to="/admin/notifications">{unreadCount ?? 0} avisos sin leer</Link>
        </div>
      </section>

      <section className="adm-home__section" aria-labelledby="adm-home-curso">
        <div className="sa-section-head">
          <h2 id="adm-home-curso" className="adm-home__h">Tus torneos en curso</h2>
          <Link className="adm-home__more" to="/admin/tournaments">
            Ver todos ›
          </Link>
        </div>
        {inProgress.length > 0 ? (
          <div className="adm-tourn__cards">
            {inProgress.map((row) => (
              <InProgressCard key={row.id} row={row} />
            ))}
          </div>
        ) : (
          <p className="muted">
            {loading ? "Cargando…" : "No tienes torneos en curso. Crea uno o abre tus borradores en «Ver todos»."}
          </p>
        )}
      </section>

      <nav className="sa-shortcuts" aria-label="Atajos">
        <Link className="sa-shortcuts__tile" to="/admin/finance">
          <strong>Finanzas</strong>
          <small>Pagos recibidos y pendientes</small>
        </Link>
        <Link className="sa-shortcuts__tile" to="/admin/payments">
          <strong>Cobros con Mercado Pago</strong>
          <small>{mpConnected ? "Cuenta conectada ✓" : "Conectar"}</small>
        </Link>
        <Link className="sa-shortcuts__tile" to="/admin/players">
          <strong>Jugadores</strong>
          <small>Catálogo y PIN</small>
        </Link>
        <Link className="sa-shortcuts__tile" to="/admin/notifications">
          <strong>Avisos</strong>
          <small>{unreadCount ? `${unreadCount} sin leer` : "Todo leído"}</small>
        </Link>
      </nav>

      <section className="admin-home-below-grid">
        <div className="card">
          <div className="card__header-row">
            <div>
              <h2 className="card__title">Próximos partidos</h2>
            </div>
            <Link className="btn btn--ghost" to={primaryTournament ? `/admin/tournaments/${primaryTournament.id}/schedule` : "/admin/tournaments"}>
              Calendario
            </Link>
          </div>
          <div className="dashboard-data-list">
            {data && data.upcoming_matches.length > 0 ? (
              data.upcoming_matches.slice(0, 4).map((match) => (
                <DashboardDataItem
                  key={match.id}
                  badge={dateBadge(match.scheduled_date)}
                  title={matchTitle(match)}
                  subtitle={matchMeta(match)}
                  tag={formatStatusLabel(match.status)}
                  to={`/admin/tournaments/${match.tournament_id}/schedule`}
                />
              ))
            ) : (
              <p className="muted">{loading ? "Cargando…" : "No hay partidos próximos programados."}</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card__header-row">
            <div>
              <h2 className="card__title">Comunicados y reglas</h2>
              <p className="card__subtitle">Lo publicado y lo pendiente de redactar.</p>
            </div>
            <Link className="btn btn--ghost" to={primaryTournament ? `/admin/tournaments/${primaryTournament.id}/communications` : "/admin/tournaments"}>
              Nuevo
            </Link>
          </div>
          <div className="dashboard-data-list">
            <DashboardDataItem
              badge={data && data.rules.pending_acceptances > 0 ? String(data.rules.pending_acceptances) : "OK"}
              title="Reglamento vigente"
              subtitle={`${data?.rules.pending_acceptances ?? 0} aceptaciones pendientes`}
              tag={data && data.rules.pending_acceptances > 0 ? "Revisar" : "Al día"}
              to={primaryTournament ? `/admin/tournaments/${primaryTournament.id}/rules` : "/admin/tournaments"}
            />
            <DashboardDataItem
              badge={String(data?.communications.draft ?? 0)}
              title="Comunicados en borrador"
              subtitle={`${data?.communications.published ?? 0} publicados`}
              tag={data && data.communications.draft > 0 ? "Publicar" : "Limpio"}
              to={primaryTournament ? `/admin/tournaments/${primaryTournament.id}/communications` : "/admin/tournaments"}
            />
            <DashboardDataItem
              badge={String(unreadCount ?? 0)}
              title="Avisos sin leer"
              subtitle="Notificaciones administrativas."
              tag={unreadCount && unreadCount > 0 ? "Ver" : "Al día"}
              to="/admin/notifications"
            />
          </div>
          {data && data.recent_communications.length > 0 && (
            <ul className="dashboard-comm-list admin-home-comm-list">
              {data.recent_communications.slice(0, 3).map((c) => (
                <CommLine key={c.id} comm={c} />
              ))}
            </ul>
          )}
        </div>
      </section>

    </div>
  );
}
